import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  Alert,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { debounce } from "lodash";
import {
  getDivisionUnits,
  ApproveSolistapi,
  getTCNumbers, // Updated import
  createIssueMTC,
  updateIssueMTC,
  getIssueMTCDetail,
} from "../../../Services/Services.jsx";

const IssueMTCForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // -------------------- State --------------------
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [rows, setRows] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [validationMessages, setValidationMessages] = useState([]);
  const [tcNumbers, setTcNumbers] = useState([]);
  const [tcLoading, setTcLoading] = useState(false);

  // Edit mode detection
  const isEditMode = !!location.state?.data?.id;
  const editId = location.state?.data?.id || null;

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState({
    division: storedDivision?.division_id || "",
    project: null,
    item_name: "",
    drawing_no: "",
    ibr_form_no: "",
  });

  // -------------------- Utility --------------------
  const createEmptyRow = () => ({
    tc_no: null,
    grade: "",
    qty: "",
  });

  // -------------------- Fetch Initial Dropdown Data --------------------
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setPageLoading(true);

        const [divisionData, tcData] = await Promise.all([
          getDivisionUnits(),
          getTCNumbers({ search: "" }), // Updated API call
        ]);

        setDivisionOptions(divisionData?.divisions || []);
        setTcNumbers(tcData?.results || []); // Handle response format
        setRows([createEmptyRow()]);
        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to fetch dropdown data");
      } finally {
        setPageLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // -------------------- Fetch Projects Based on Division --------------------
  const fetchProjects = async (divisionId = "", search = "") => {
    if (!divisionId) {
      setProjectOptions([]);
      return;
    }

    try {
      setProjectLoading(true);

      const response = await ApproveSolistapi(1, {
        division: divisionId,
        project_name: search,
        status: "Approved",
      });

      // Handle both array and object response
      const projects = Array.isArray(response)
        ? response
        : response?.results || [];
      setProjectOptions(projects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  // Debounced project search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (formData.division) {
        fetchProjects(formData.division, projectSearch);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [projectSearch, formData.division]);

  // -------------------- Fetch TC Numbers with Search --------------------
  const fetchTCNumbers = useCallback(
    debounce(async (search = "") => {
      try {
        setTcLoading(true);

        const response = await getTCNumbers({ search }); // Updated API call
        setTcNumbers(response?.results || []); // Handle response format
      } catch (error) {
        console.error("Error fetching TC numbers:", error);
      } finally {
        setTcLoading(false);
      }
    }, 500),
    [],
  );

  // -------------------- Populate Data in Edit Mode --------------------
  useEffect(() => {
    const fetchEditData = async () => {
      if (isEditMode && editId && isDataLoaded) {
        try {
          setPageLoading(true);

          const response = await getIssueMTCDetail(editId);

          if (!response?.success) {
            toast.error(response?.error || "Failed to load Issue MTC data");
            return;
          }

          const incoming = response.data || {};

          // Fetch project options for the division first
          let projectsList = [];
          if (incoming.division) {
            const projectsResponse = await ApproveSolistapi(1, {
              division: incoming.division,
              status: "Approved",
            });
            projectsList = Array.isArray(projectsResponse)
              ? projectsResponse
              : projectsResponse?.results || [];
            setProjectOptions(projectsList);
          }

          // Find the project object from the list
          const selectedProject =
            projectsList.find(
              (p) =>
                p.id === incoming.project ||
                p.project_name === incoming.project_name,
            ) || null;

          // Set form data
          setFormData({
            division: incoming.division || "",
            project: selectedProject,
            item_name: incoming.item_name || "",
            drawing_no: incoming.drawing_no || "",
            ibr_form_no: incoming.ibr_form_no || "",
          });

          // Map items
          const mappedItems = (incoming.items || []).map((item) => {
            // Find TC from loaded tcNumbers or create object from response
            const foundTc = tcNumbers.find((tc) => tc.id === item.tc_no) || {
              id: item.tc_no,
              tc_no: item.tc_no_display || "",
              material: item.material || item.grade || "",
            };

            return {
              tc_no: foundTc,
              grade: item.grade || "",
              qty: item.qty || "",
            };
          });

          setRows(mappedItems.length > 0 ? mappedItems : [createEmptyRow()]);
        } catch (error) {
          console.error("Error fetching edit data:", error);
          toast.error("Failed to load Issue MTC data");
        } finally {
          setPageLoading(false);
        }
      }
    };

    fetchEditData();
  }, [isEditMode, editId, isDataLoaded, tcNumbers]);

  // -------------------- Handle Form Change --------------------
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
    setShowValidationSummary(false);

    if (field === "division") {
      // Reset project when division changes
      setFormData((prev) => ({ ...prev, project: null }));
      setProjectSearch("");
      if (value) {
        fetchProjects(value, "");
      } else {
        setProjectOptions([]);
      }
    }
  };

  // -------------------- Handle Row Change --------------------
  const handleInputChange = (index, key, value) => {
    const updatedRows = [...rows];
    let row = { ...updatedRows[index] };

    if (key === "tc_no") {
      row.tc_no = value;
      row.grade = value?.material || "";
    } else {
      row[key] = value;
    }

    updatedRows[index] = row;
    setRows(updatedRows);

    // Clear row errors
    setRowErrors((prev) => {
      const newErrors = [...prev];
      if (newErrors[index]) {
        newErrors[index] = { ...newErrors[index], [key]: "" };
      }
      return newErrors;
    });
  };

  // Handle TC Number search input
  const handleTcInputChange = (newValue) => {
    fetchTCNumbers(newValue);
  };

  // -------------------- Add / Delete Rows --------------------
  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setRowErrors((prev) => [...prev, {}]);
    setShowValidationSummary(false);
  };

  const handleDeleteRow = (index) => {
    if (rows.length === 1) {
      toast.warning("At least one item is required");
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => prev.filter((_, i) => i !== index));
    setShowValidationSummary(false);
  };

  // -------------------- Validate Form --------------------
  const validateForm = () => {
    const errors = {};
    const rowErrs = [];
    const messages = [];

    // Header validations
    if (!formData.division) {
      errors.division = "Division is required";
      messages.push("Division is required");
    }
    if (!formData.project) {
      errors.project = "Project is required";
      messages.push("Project is required");
    }
    if (!formData.item_name?.trim()) {
      errors.item_name = "Item Name is required";
      messages.push("Item Name is required");
    }
    if (!formData.drawing_no?.trim()) {
      errors.drawing_no = "Drawing No is required";
      messages.push("Drawing No is required");
    }
    if (!formData.ibr_form_no?.trim()) {
      errors.ibr_form_no = "IBR Form No is required";
      messages.push("IBR Form No is required");
    }

    // Row validations
    rows.forEach((row, i) => {
      const err = {};

      if (!row.tc_no) {
        err.tc_no = "Required";
        messages.push(`Row ${i + 1}: TC No is required`);
      }

      const qty = parseFloat(row.qty);
      if (!row.qty || isNaN(qty) || qty <= 0) {
        err.qty = "Required (must be > 0)";
        messages.push(`Row ${i + 1}: Qty must be greater than 0`);
      }

      rowErrs[i] = err;
    });

    // Check for at least one row
    if (rows.length === 0) {
      messages.push("At least one item is required");
    }

    // Check for duplicate TC numbers
    const tcNos = rows.map((row) => row.tc_no?.id).filter(Boolean);
    const duplicates = tcNos.filter((id, index) => tcNos.indexOf(id) !== index);
    if (duplicates.length > 0) {
      messages.push("Duplicate TC Numbers are not allowed");
    }

    setFormErrors(errors);
    setRowErrors(rowErrs);
    setValidationMessages(messages);
    setShowValidationSummary(messages.length > 0);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0) &&
      duplicates.length === 0
    );
  };

  // -------------------- Reset Form --------------------
  const resetForm = () => {
    setFormData({
      division: "",
      project: null,
      item_name: "",
      drawing_no: "",
      ibr_form_no: "",
    });
    setRows([createEmptyRow()]);
    setFormErrors({});
    setRowErrors([]);
    setValidationMessages([]);
    setShowValidationSummary(false);
    setProjectOptions([]);
    setProjectSearch("");
  };

  // -------------------- Submit Form --------------------
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);

      // Prepare payload
      const payload = {
        division: formData.division,
        project: formData.project?.id,
        item_name: formData.item_name.trim(),
        drawing_no: formData.drawing_no.trim(),
        ibr_form_no: formData.ibr_form_no.trim(),
        items: rows.map((row) => ({
          tc_no: row.tc_no?.id,
          grade: row.grade || row.tc_no?.material || "",
          qty: parseFloat(row.qty).toFixed(2),
        })),
      };

      let response;
      if (isEditMode) {
        response = await updateIssueMTC(editId, payload);
      } else {
        response = await createIssueMTC(payload);
      }

      if (response?.success) {
        toast.success(
          response.message ||
          `Issue MTC ${isEditMode ? "updated" : "created"} successfully!`,
        );

        if (isEditMode) {
          setTimeout(() => {
            navigate("/dashboard/issue-mtc-list");
          }, 1500);
        } else {
          resetForm();
        }
      } else {
        // Handle error response
        const errorMsg = response?.error || response?.details;
        if (typeof errorMsg === "object") {
          Object.entries(errorMsg).forEach(([key, value]) => {
            toast.error(
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
            );
          });
        } else {
          toast.error(errorMsg || "Something went wrong");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Something went wrong. Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- Handle Back --------------------
  const handleBack = () => {
    navigate("/dashboard/issue-mtc-list");
  };

  // -------------------- Loading State --------------------
  if (pageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // -------------------- JSX --------------------
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Validation Summary Alert */}
      {showValidationSummary && validationMessages.length > 0 && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setShowValidationSummary(false)}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Please fix the following errors:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            {validationMessages.map((msg, idx) => (
              <li key={idx}>
                <Typography variant="body2">{msg}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Header with Buttons */}
      <Box sx={{ mt: -4, pt: 0, mb: 2, px: 1, py: 1 }}>
        <Grid
          container
          justifyContent={isMobile ? "center" : "flex-end"}
          gap={2}
          mb={2}
          direction={isMobile ? "column" : "row"}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 100,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="outlined"
            onClick={resetForm}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 100,
              fontSize: "0.7rem",
              textTransform: "none",
              color: "#ff8400",
              borderColor: "#ff8400",
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 120,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Grid>
      </Box>

      {/* Form Fields */}
      <Box border="1px solid #ccc" borderRadius="8px" p={2} bgcolor="#fff">
        {/* Page Title */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          {isEditMode ? "Edit Issue MTC" : "Create Issue MTC"}
        </Typography>

        {/* First Row: Division, Project, Item Name */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Division */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Division"
              size="small"
              value={storedDivision?.division || "No Division Selected"}
              disabled
              InputProps={{
                sx: {
                  backgroundColor: "#f5f5f5",
                  height: 40,
                },
              }}
              helperText="Division is set globally"
              error={!storedDivision?.division_id}
            />
          </Grid>

          {/* Project */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              fullWidth
              options={projectOptions}
              getOptionLabel={(opt) => opt.project_name || ""}
              value={formData.project}
              onChange={(e, val) => handleFormChange("project", val)}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === "input") {
                  setProjectSearch(newInputValue);
                }
              }}
              loading={projectLoading}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              noOptionsText={
                formData.division
                  ? "No projects found"
                  : "Select division first"
              }
              disabled={!formData.division}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Project"
                  error={!!formErrors.project}
                  helperText={
                    formErrors.project ||
                    (!formData.division ? "Select division first" : "")
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {projectLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Item Name */}
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              required
              fullWidth
              label="Item Name"
              value={formData.item_name}
              onChange={(e) => handleFormChange("item_name", e.target.value)}
              error={!!formErrors.item_name}
              helperText={formErrors.item_name}
            />
          </Grid>
        </Grid>

        {/* Second Row: Drawing No, IBR Form No */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Drawing No */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              fullWidth
              label="Drawing No"
              value={formData.drawing_no}
              onChange={(e) => handleFormChange("drawing_no", e.target.value)}
              error={!!formErrors.drawing_no}
              helperText={formErrors.drawing_no}
            />
          </Grid>

          {/* IBR Form No */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              fullWidth
              label="IBR Form No"
              value={formData.ibr_form_no}
              onChange={(e) => handleFormChange("ibr_form_no", e.target.value)}
              error={!!formErrors.ibr_form_no}
              helperText={formErrors.ibr_form_no}
            />
          </Grid>
        </Grid>

        {/* Table Section */}
        <Box mt={3}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            Items
          </Typography>

          {isMobile ? (
            // Mobile Card View
            <Box>
              {rows.map((row, index) => (
                <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">Item #{index + 1}</Typography>
                      <IconButton
                        onClick={() => handleDeleteRow(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* TC No */}
                      <Box>
                        <Autocomplete
                          options={tcNumbers}
                          getOptionLabel={(opt) => opt.tc_no || ""}
                          value={row.tc_no || null}
                          onChange={(e, val) =>
                            handleInputChange(index, "tc_no", val)
                          }
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleTcInputChange(newValue);
                            }
                          }}
                          loading={tcLoading}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value?.id
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="TC No"
                              required
                              size="small"
                              error={!!rowErrors[index]?.tc_no}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {tcLoading && (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box>
                                <Typography variant="body2">
                                  {option.tc_no}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Material: {option.material || "N/A"}
                                </Typography>
                              </Box>
                            </li>
                          )}
                        />
                        {rowErrors[index]?.tc_no && (
                          <FormHelperText error sx={{ ml: 2 }}>
                            {rowErrors[index].tc_no}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* Grade */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Grade (Auto-filled)"
                        value={row.grade}
                        disabled
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />

                      {/* Qty */}
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        label="Qty"
                        value={row.qty}
                        onChange={(e) =>
                          handleInputChange(index, "qty", e.target.value)
                        }
                        error={!!rowErrors[index]?.qty}
                        helperText={rowErrors[index]?.qty}
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer sx={{ border: "1px solid #ddd", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "2px solid #999",
                      },
                    }}
                  >
                    <TableCell sx={{ width: 80 }}>S.No</TableCell>
                    <TableCell sx={{ minWidth: 250 }}>TC No *</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Grade</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Qty *</TableCell>
                    <TableCell sx={{ width: 80, textAlign: "center" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          rowErrors[index] &&
                            Object.keys(rowErrors[index]).length > 0
                            ? "#ffebee"
                            : "inherit",
                        "&:hover": {
                          backgroundColor: "#fafafa",
                        },
                      }}
                    >
                      <TableCell sx={{ textAlign: "center", fontWeight: 500 }}>
                        {index + 1}
                      </TableCell>

                      {/* TC No */}
                      <TableCell>
                        <Box>
                          <Autocomplete
                            size="small"
                            options={tcNumbers}
                            getOptionLabel={(opt) => opt.tc_no || ""}
                            value={row.tc_no || null}
                            onChange={(e, val) =>
                              handleInputChange(index, "tc_no", val)
                            }
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") {
                                handleTcInputChange(newValue);
                              }
                            }}
                            loading={tcLoading}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value?.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select TC No"
                                error={!!rowErrors[index]?.tc_no}
                                InputProps={{
                                  ...params.InputProps,
                                  style: { height: 40 },
                                  endAdornment: (
                                    <>
                                      {tcLoading && (
                                        <CircularProgress
                                          color="inherit"
                                          size={18}
                                        />
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <Typography variant="body2">
                                    {option.tc_no}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {option.material || ""}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                            sx={{ minWidth: 220 }}
                          />
                          {rowErrors[index]?.tc_no && (
                            <FormHelperText
                              error
                              sx={{ ml: 1, fontSize: "0.7rem" }}
                            >
                              {rowErrors[index].tc_no}
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>

                      {/* Grade */}
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.grade}
                          disabled
                          placeholder="Auto-filled"
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              height: 40,
                              backgroundColor: "#f9f9f9",
                            },
                          }}
                        />
                      </TableCell>

                      {/* Qty */}
                      <TableCell>
                        <Box>
                          <TextField
                            type="number"
                            size="small"
                            value={row.qty}
                            onChange={(e) =>
                              handleInputChange(index, "qty", e.target.value)
                            }
                            placeholder="Enter Qty"
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": {
                                height: 40,
                              },
                            }}
                            error={!!rowErrors[index]?.qty}
                          />
                          {rowErrors[index]?.qty && (
                            <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                              {rowErrors[index].qty}
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>

                      {/* Delete */}
                      <TableCell sx={{ textAlign: "center" }}>
                        <Tooltip title="Delete Row">
                          <IconButton
                            onClick={() => handleDeleteRow(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Add Row Button */}
          <Box mt={2} textAlign="center">
            <Tooltip title="Add New Row">
              <IconButton onClick={handleAddRow} color="primary" size="large">
                <AddCircleOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Click to add more items
            </Typography>
          </Box>

          {/* Summary */}
          {rows.length > 0 && (
            <Box
              mt={2}
              p={2}
              bgcolor="#f5f5f5"
              borderRadius={1}
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Typography variant="body2">
                <strong>Total Items:</strong> {rows.length}
              </Typography>
              <Typography variant="body2">
                <strong>Total Qty:</strong>{" "}
                {rows
                  .reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default IssueMTCForm;
